// src/features/inventory/pages/TestBarcodesPage/index.jsx
//
// Hidden page (no menu entry): /inventory/test-barcodes. Printable test codes
// for demoing and checking the phone scanner, built from this department's
// real items and lots.
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Card, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Print as PrintIcon,
  QrCodeScanner as ScannerIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import PageHeader from '../../../../components/common/PageHeader';
import { itemsAPI } from '../../services/items';
import { lotsAPI } from '../../services/lots';
import { productGroupsAPI } from '../../services/productGroups';
import { buildTestCodes } from './testCodes';

function BarcodeImage({ symbology, text }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Loaded on demand so the barcode renderer stays out of the main bundle
    import('bwip-js/browser')
      .then(({ toSVG }) => {
        // EAN-13 is linear, with its standard digits under the bars. DataMatrix
        // codes are square and textless; the card prints the value under them
        const svg = toSVG(symbology === 'ean13'
          ? { bcid: symbology, text, scale: 3, height: 14, includetext: true }
          : { bcid: symbology, text, scale: 5 });
        if (!cancelled) setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => { cancelled = true; };
  }, [symbology, text]);

  if (failed) return <Typography color="error" variant="body2">{text}</Typography>;
  if (!src) return <CircularProgress size={24} />;
  return <Box component="img" src={src} alt={text} sx={{ maxWidth: '100%', height: 110, objectFit: 'contain' }} />;
}

function CodeCard({ code, t }) {
  const [copied, setCopied] = useState(false);
  const base = `inventory:test_barcodes.cases.${code.key}`;
  const params = { item: code.item?.name, sku: code.item?.sku, lot: code.lot?.lot_number, ...code.params };

  const copy = async () => {
    await navigator.clipboard.writeText(code.value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Card variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, breakInside: 'avoid' }}>
      <Typography variant="subtitle1" fontWeight={600}>{t(`${base}.title`, params)}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>{t(`${base}.expect`, params)}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#fff', borderRadius: 1, p: 1.5, minHeight: 134 }}>
        <BarcodeImage symbology={code.symbology} text={code.text} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {t(`inventory:test_barcodes.symbology.${code.symbology}`)}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
          {code.value}
        </Typography>
        <Tooltip title={copied ? t('inventory:test_barcodes.copied') : t('inventory:test_barcodes.copy')}>
          <IconButton size="small" onClick={copy} aria-label={t('inventory:test_barcodes.copy')} sx={{ displayPrint: 'none' }}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Card>
  );
}

export default function TestBarcodesPage() {
  const { t } = useTranslation(['inventory']);
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setCodes(null);
    setError(null);
    try {
      const [itemsRes, groupsRes, lotsRes] = await Promise.all([
        itemsAPI.getItems(),
        productGroupsAPI.getProductGroups(),
        lotsAPI.filterLots({ limit: 1000 }),
      ]);
      setCodes(buildTestCodes({
        items: itemsRes.data?.data ?? [],
        groups: groupsRes.data?.data ?? [],
        lots: lotsRes.data?.data ?? [],
        today: new Date().toLocaleDateString('sv-SE'), // YYYY-MM-DD, local time
      }));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <PageHeader
        icon={ScannerIcon}
        title={t('inventory:test_barcodes.title')}
        subtitle={t('inventory:test_barcodes.subtitle')}
        actions={<Box sx={{ display: 'flex', gap: 1, displayPrint: 'none' }}>
          <Button component={RouterLink} to="/inventory/scanner" variant="outlined" startIcon={<ScannerIcon />}>
            {t('inventory:test_barcodes.open_scanner')}
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
            {t('inventory:test_barcodes.print')}
          </Button>
          <Tooltip title={t('inventory:test_barcodes.refresh')}>
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>}
      />

      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1, sm: 2 } }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!codes && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        )}
        {codes && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {codes.map(code => <CodeCard key={code.key} code={code} t={t} />)}
          </Box>
        )}
      </Box>
    </Box>
  );
}
